import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import useTranslation from "../../hooks/useTranslation";
import { cn } from "../../utils";
import AnalyzeMyTradingButton from "../../../edgy/AnalyzeMyTradingButton";
import EdgyDrawer from "../../../edgy/EdgyDrawer";
import NotificationsBell from "../../../notifications/NotificationsBell";
import { useQuery, getMyStudents } from "wasp/client/operations";
import {
  getModules,
  moduleForPath,
  getRememberedTab,
  canAccessMentor,
  buildCustomPagesGroup,
  buildStudentEntries,
  studentIdFromPath,
  NO_STUDENT_PERMISSIONS,
  type ModuleId,
} from "./navConfig";

/**
 * Mobile counterpart of the two-level navigation:
 * level 1 becomes a burger sheet, level 2 a horizontally scrollable tab strip.
 */
export default function MobileNavigation2({
  userPlan,
  customPages = [],
}: {
  userPlan: string | null | undefined;
  customPages?: Array<{ id: string; name: string; icon?: string }>;
}) {
  const t = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [edgyOpen, setEdgyOpen] = useState(false);

  const modules = getModules(t);
  const activeModuleId: ModuleId = moduleForPath(location.pathname, modules);
  const activeModule = modules.find((m) => m.id === activeModuleId);
  const mentorUnlocked = canAccessMentor(userPlan);

  // The secondary sidebar is desktop-only, so the open student's pages have to be
  // reachable from here too — otherwise removing the horizontal tab bar would
  // strand them on mobile.
  const openStudentId = studentIdFromPath(location.pathname);
  const { data: mentorStudents = [] } = useQuery(
    getMyStudents,
    undefined,
    { enabled: openStudentId != null }
  );
  const openStudent = openStudentId
    ? (mentorStudents as any[]).find((s) => s.studentId === openStudentId)
    : undefined;

  const tabs = [
    ...(activeModule?.entries ?? []),
    ...(activeModule?.id === "mentor" && openStudentId && openStudent
      ? buildStudentEntries(t, openStudentId, openStudent.permissions ?? NO_STUDENT_PERMISSIONS)
      : []),
    ...(activeModule?.id === "dashboard"
      ? buildCustomPagesGroup(t, customPages).entries
      : []),
    ...(activeModule?.groups?.flatMap((g) => g.entries) ?? []),
  ];

  return (
    <>
      <div className="lg:hidden sticky top-0 z-40 flex items-center gap-2 border-b border-border bg-navRail px-3 py-2">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={t.newNav.menu}
          className="flex h-9 w-9 items-center justify-center rounded-md text-white/90 hover:bg-white/10"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          edgely<span className="text-navRail-accent">.</span>
        </span>

        {/* Cloche. Le rail desktop est masque sous lg : sans cette entree, les
            notifications seraient invisibles sur telephone. Placee avant Edgy
            pour qu'Edgy reste l'action principale la plus a droite. */}
        <div className="ml-auto">
          <NotificationsBell />
        </div>

        {/* Edgy. The desktop rail is hidden below lg, so without this entry an
            analysis could not be launched at all on mobile. */}
        <AnalyzeMyTradingButton
          onClick={() => setEdgyOpen(true)}
          compact
        />
      </div>

      {/* Level 2 as a scrollable tab strip. */}
      {tabs.length > 0 && (
        <div className="lg:hidden sticky top-[52px] z-30 border-b border-border bg-card">
          <div className="flex gap-1 overflow-x-auto px-2 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((entry) => {
              const isActive = location.pathname === entry.to;
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => navigate(entry.to)}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors",
                    isActive
                      ? "bg-primary font-medium text-primary-foreground"
                      : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {entry.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Level 1 sheet. */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[880]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} aria-hidden />
          <nav
            className="absolute left-0 top-0 flex h-full w-64 flex-col bg-navRail p-3"
            aria-label={t.newNav.menu}
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="text-lg font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                edgely<span className="text-navRail-accent">.</span>
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t.edgy.close}
                className="flex h-8 w-8 items-center justify-center rounded-md text-white/80 hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ul className="flex flex-col gap-1">
              {modules.map((module) => {
                const Icon = module.icon;
                const isActive = activeModuleId === module.id;
                const locked = module.requiresPlan === true && !mentorUnlocked;

                return (
                  <li key={module.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate(locked ? "/pricing" : getRememberedTab(module.id) ?? module.defaultPath);
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                        isActive ? "bg-navRail-active text-white" : "text-white/80 hover:bg-white/10"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate">{module.label}</span>
                      {locked && <Lock className="h-3.5 w-3.5 opacity-70" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      )}

      <EdgyDrawer open={edgyOpen} onClose={() => setEdgyOpen(false)} />
    </>
  );
}
